import { useQuery } from "@tanstack/react-query";
import { Copy, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { useInvalidateAll } from "../../lib/queries";
import type { ApiEnvelope } from "../../types";
import { Feedback, Field, Pill, useAction } from "./shared";

type CheckStatus = "pass" | "fail" | "unknown";
interface ClosureCheck { id: string; label: string; status: CheckStatus; detail: string }
interface ClosureState {
  contract: string | null;
  clabe: string | null;
  additionalContracts: string[];
  closure: {
    id: string; contract: string; status: "requested" | "confirmed" | "withdrawn"; kuspitTicket: string | null;
    unverifiedAcknowledged: boolean; notes: string | null; requestedAt: string; resolvedAt: string | null;
  } | null;
  preflight: { checks: ClosureCheck[]; checkedAt: string } | null;
  ready: boolean;
  ticket: { title: string; body: string } | null;
}

const CHECK_TONE: Record<CheckStatus, { tone: "ok" | "bad" | "warn"; label: string }> = {
  pass: { tone: "ok", label: "cumple" },
  fail: { tone: "bad", label: "no cumple" },
  unknown: { tone: "warn", label: "sin respuesta" }
};

/**
 * Closing a customer's Kuspit contract (ticket G, 2026-09-22). Kuspit has no API for it: the admin
 * checks Kuspit's conditions here, files the drafted text as a Kuspit support ticket, records the
 * ticket number (which also closes the Aventuria account) and, when Kuspit answers, confirms it.
 * The contract and CLABE are deactivated by Kuspit but never deleted — nor is anything on our side.
 */
export function KuspitClosureCard({ userId }: { userId: string }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [ticket, setTicket] = useState("");
  const [notes, setNotes] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [copied, setCopied] = useState(false);
  const state = useQuery({
    queryKey: ["admin", "closure", userId],
    queryFn: () => apiRequest<ApiEnvelope<ClosureState>>(`/api/v1/admin/users/${userId}/closure`),
    select: (payload) => payload.data,
    enabled: false
  });
  const data = state.data;
  const closure = data?.closure && data.closure.status !== "withdrawn" ? data.closure : null;
  const unknown = data?.preflight?.checks.filter((check) => check.status === "unknown") ?? [];
  const failed = data?.preflight?.checks.filter((check) => check.status === "fail") ?? [];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("request", async () => {
      await apiRequest(`/api/v1/admin/users/${userId}/closure`, {
        method: "POST",
        body: JSON.stringify({ kuspitTicket: ticket.trim(), acknowledgeUnverified: acknowledge, ...(notes.trim() ? { notes: notes.trim() } : {}) })
      });
      await invalidate();
      await state.refetch();
      return "Solicitud registrada. La cuenta de Aventuria quedó cerrada; confirma aquí cuando Kuspit cancele el contrato.";
    });
  };
  const resolve = (target: "confirm" | "withdraw") => action.run(target, async () => {
    await apiRequest(`/api/v1/admin/users/${userId}/closure/${target}`, { method: "POST", body: JSON.stringify(notes.trim() ? { notes: notes.trim() } : {}) });
    await invalidate();
    await state.refetch();
    return target === "confirm" ? "Cancelación confirmada por Kuspit." : "Solicitud retirada. Puedes reactivar la cuenta desde el estado del perfil.";
  });
  const copyTicket = async () => {
    if (!data?.ticket) return;
    try {
      await navigator.clipboard.writeText(`${data.ticket.title}\n\n${data.ticket.body}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="admin-stack tight closure-block">
      <div className="section-heading"><div><span className="page-eyebrow">Kuspit</span><h3>Cerrar contrato Kuspit</h3></div></div>
      <p className="field-hint">
        Kuspit cancela un contrato por ticket de soporte, con saldo en $0.00, sin posiciones en fondos y sin SPEI en curso.
        El contrato y la CLABE quedan inactivos pero Kuspit los conserva de 5 a 10 años; aquí tampoco se borra nada.
      </p>
      {!data && (
        <button type="button" className="secondary-button small" onClick={() => void state.refetch()} disabled={state.isFetching}>
          {state.isFetching ? <Spinner /> : <ShieldCheck size={14} aria-hidden="true" />} Verificar requisitos con Kuspit
        </button>
      )}
      {state.isError && <div className="form-error" role="alert">{state.error instanceof Error ? state.error.message : "No pudimos consultar los requisitos."}</div>}
      {data && !data.contract && <p className="field-hint">Este cliente no tiene contrato Kuspit; usa "Cerrar cuenta".</p>}
      {data && data.additionalContracts.length > 0 && (
        <div className="form-error" role="alert">El cliente tiene más de un contrato ({[data.contract, ...data.additionalContracts].join(", ")}). Kuspit permite uno por persona: aclara con Kuspit antes de continuar.</div>
      )}

      {closure && (
        <div className="wallet-sync-row">
          <div className="table-primary">
            <strong>Contrato <span translate="no">{closure.contract}</span> <Pill tone={closure.status === "confirmed" ? "ok" : "warn"} label={closure.status === "confirmed" ? "cancelado por Kuspit" : "cancelación solicitada"} /></strong>
            <span>
              Ticket {closure.kuspitTicket ?? "sin número"} · solicitado {formatTimestamp(closure.requestedAt)}
              {closure.resolvedAt ? ` · confirmado ${formatTimestamp(closure.resolvedAt)}` : ""}
              {closure.unverifiedAcknowledged ? " · algunos requisitos no pudieron verificarse con Kuspit" : ""}
            </span>
          </div>
          {closure.status === "requested" && (
            <div className="row-actions">
              <button type="button" className="secondary-button small" onClick={() => void resolve("confirm")} disabled={action.busy !== null}>
                {action.busy === "confirm" ? <Spinner /> : null} Kuspit confirmó
              </button>
              <button type="button" className="secondary-button small danger" onClick={() => void resolve("withdraw")} disabled={action.busy !== null}>
                {action.busy === "withdraw" ? <Spinner /> : null} Retirar solicitud
              </button>
            </div>
          )}
        </div>
      )}

      {data?.preflight && !closure && (
        <>
          <ul className="closure-checks">
            {data.preflight.checks.map((check) => (
              <li key={check.id}>
                <Pill tone={CHECK_TONE[check.status].tone} label={CHECK_TONE[check.status].label} />
                <div><strong>{check.label}</strong><span>{check.detail}</span></div>
              </li>
            ))}
          </ul>
          <span className="field-hint">Verificado {formatTimestamp(data.preflight.checkedAt)} · <button type="button" className="link-button" onClick={() => void state.refetch()} disabled={state.isFetching}>{state.isFetching ? "Verificando…" : "Volver a verificar"}</button></span>
          {failed.length === 0 && data.ticket && (
            <form className="admin-stack tight" onSubmit={submit}>
              <Field label="Texto para el ticket de Kuspit" htmlFor="closure-ticket-text" hint="Ábrelo en el portal de soporte de Kuspit y registra abajo el número que te asignen.">
                <textarea id="closure-ticket-text" className="standalone-input textarea" readOnly rows={8} value={`${data.ticket.title}\n\n${data.ticket.body}`} />
              </Field>
              <button type="button" className="link-button" onClick={() => void copyTicket()}><Copy size={13} aria-hidden="true" /> {copied ? "Copiado" : "Copiar texto"}</button>
              <Field label="Número de ticket en Kuspit" htmlFor="closure-ticket">
                <input id="closure-ticket" className="standalone-input" value={ticket} onChange={(event) => setTicket(event.target.value)} minLength={3} maxLength={40} placeholder="SCEQA-000" required />
              </Field>
              <Field label="Notas (opcional)" htmlFor="closure-notes">
                <input id="closure-notes" className="standalone-input" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} />
              </Field>
              {unknown.length > 0 && (
                <label className="checkbox-row">
                  <input type="checkbox" checked={acknowledge} onChange={(event) => setAcknowledge(event.target.checked)} />
                  <span>Kuspit no respondió {unknown.map((check) => check.label.toLowerCase()).join(" y ")}. Lo verifiqué directamente con Kuspit.</span>
                </label>
              )}
              <button className="primary-button solid" type="submit" disabled={action.busy !== null || ticket.trim().length < 3 || (unknown.length > 0 && !acknowledge)}>
                {action.busy === "request" ? <Spinner /> : null} Registrar solicitud y cerrar la cuenta
              </button>
            </form>
          )}
          {failed.length > 0 && <p className="field-hint">Resuelve los requisitos marcados antes de abrir el ticket: el cliente debe vender sus fondos y retirar todo su saldo.</p>}
        </>
      )}
      {closure?.status === "requested" && (
        <Field label="Notas para la bitácora (opcional)" htmlFor="closure-resolve-notes">
          <input id="closure-resolve-notes" className="standalone-input" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} />
        </Field>
      )}
      <Feedback error={action.error} notice={action.notice} />
    </div>
  );
}
